import { ledgerEntries } from '@/mocks/members';
import type { Member } from '@/types/member';

interface MemberDrawerProps {
  member: Member | null;
  onClose: () => void;
}

const typeStyles: Record<string, { icon: string; classes: string }> = {
  earn: { icon: 'ri-add-line', classes: 'bg-accent-100 text-accent-700' },
  redeem: { icon: 'ri-gift-2-line', classes: 'bg-secondary-100 text-secondary-700' },
  adjust: { icon: 'ri-equalizer-line', classes: 'bg-background-200 text-foreground-700' },
};

export default function MemberDrawer({ member, onClose }: MemberDrawerProps) {
  if (!member) return null;

  const history = ledgerEntries.filter((entry) => entry.memberId === member.id).slice(0, 6);

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-foreground-950/40" onClick={onClose} aria-hidden="true"></div>

      <aside className="relative w-full max-w-md h-full bg-background-50 border-l border-background-200 flex flex-col overflow-y-auto">
        <div className="sticky top-0 bg-background-50/95 backdrop-blur-md border-b border-background-200/70 h-16 px-5 flex items-center justify-between shrink-0">
          <h3 className="font-heading font-semibold text-foreground-950">Member details</h3>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-md bg-background-100 text-foreground-600 hover:text-foreground-950 cursor-pointer"
            aria-label="Close details"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="p-5 md:p-6 flex flex-col gap-6">
          <div className="flex items-center gap-4">
            <span className="w-14 h-14 shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 font-heading font-bold text-lg">
              {member.name
                .split(' ')
                .map((part) => part[0])
                .join('')
                .slice(0, 2)}
            </span>
            <div className="min-w-0">
              <p className="font-heading font-bold text-lg text-foreground-950 truncate">{member.name}</p>
              <p className="text-sm text-foreground-500 truncate">{member.email}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg bg-background-100 border border-background-200 p-3.5">
              <p className="text-xs text-foreground-500">Points balance</p>
              <p className="mt-1 font-heading font-bold text-xl text-foreground-950">
                {member.balance.toLocaleString()}
              </p>
            </div>
            <div className="rounded-lg bg-background-100 border border-background-200 p-3.5">
              <p className="text-xs text-foreground-500">Lifetime points</p>
              <p className="mt-1 font-heading font-bold text-xl text-foreground-950">
                {member.lifetime.toLocaleString()}
              </p>
            </div>
          </div>

          <dl className="flex flex-col divide-y divide-background-200/70 border-t border-b border-background-200/70">
            <div className="flex items-center justify-between py-3">
              <dt className="text-sm text-foreground-500">Tier</dt>
              <dd className="text-sm font-semibold text-foreground-900">{member.tier}</dd>
            </div>
            <div className="flex items-center justify-between py-3">
              <dt className="text-sm text-foreground-500">Connected platform</dt>
              <dd className="text-sm font-semibold text-foreground-900">{member.platform}</dd>
            </div>
            <div className="flex items-center justify-between py-3">
              <dt className="text-sm text-foreground-500">Status</dt>
              <dd className="text-sm font-semibold text-foreground-900">
                {member.status === 'active' ? 'Active' : 'Idle'}
              </dd>
            </div>
            <div className="flex items-center justify-between py-3">
              <dt className="text-sm text-foreground-500">Joined</dt>
              <dd className="text-sm font-semibold text-foreground-900">{member.joined}</dd>
            </div>
            <div className="flex items-center justify-between py-3">
              <dt className="text-sm text-foreground-500">Member ID</dt>
              <dd className="text-sm font-mono text-foreground-700">{member.id}</dd>
            </div>
          </dl>

          <div>
            <h4 className="font-heading font-semibold text-foreground-950 mb-3">Recent activity</h4>
            {history.length === 0 ? (
              <p className="text-sm text-foreground-500">No recorded activity for this member yet.</p>
            ) : (
              <ul className="flex flex-col divide-y divide-background-200/70">
                {history.map((entry) => {
                  const style = typeStyles[entry.type] ?? typeStyles.adjust;
                  const positive = entry.points > 0;
                  return (
                    <li key={entry.id} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                      <span className={`w-8 h-8 shrink-0 flex items-center justify-center rounded-full ${style.classes}`}>
                        <i className={`${style.icon} text-sm`}></i>
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-foreground-900 truncate">{entry.description}</p>
                        <p className="text-xs text-foreground-500">{entry.source} · {entry.time}</p>
                      </div>
                      <span
                        className={`text-sm font-semibold shrink-0 ${
                          positive ? 'text-accent-700' : 'text-secondary-700'
                        }`}
                      >
                        {positive ? '+' : ''}
                        {entry.points.toLocaleString()}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </aside>
    </div>
  );
}
